import { combineReducers, configureStore } from '@reduxjs/toolkit';
import HubConnectionObj from '../reducers/hub-connection';
import SwitchUserRole from '../reducers/switch-role';
import UserAvatarImg from '../reducers/useravatar-img';
import userDataSlice from '../reducers/userDataSlice';
import { persistReducer, persistStore, createTransform, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import storage from "redux-persist/lib/storage";
import notificationsSlice from '../reducers/notificationsSlice';
import chatSlice from '../reducers/chat-slice';
import applicationDataSlice from '../reducers/application-cached-data';
import personalInfoReducer from '../reducers/personalInfoSlice';
import educationReducer from '../reducers/educationSlice';
import certificationInfoReducer from '../reducers/CertificationInfoSlice';
import portfolioReducer from '../reducers/portfolioSlice';
import languageInfoReducer from '../reducers/LanguageInfoSlice';
import experienceInfoReducer from '../reducers/ExperienceInfoSlice';
import websiteInfoReducer from '../reducers/WebsiteInfoSlice';
import basicDetailsReducer from '../reducers/create-consultation-data/basicDetailsSlice';
import categorizationReducer from '../reducers/create-consultation-data/categorizationSlice';
import packagesPricingReducer from '../reducers/create-consultation-data/packagesPricingSlice';
import firstStepReducer from '../reducers/create-project-data/projectfirstStepslice';
import secondStepReducer from '../reducers/create-project-data/projectsecondStepslice';
import stepperReducer from '../reducers/stepperSlice';
import searchConsultationReducer, { clearSearchFilters, setLastDetailTimestamp, setLastExploreTimestamp, setSearchFilters } from '../reducers/search-consultation-data/search-consultation-slice';
import { rememberSearchConsultation } from '../reducers/search-consultation-data/search-filters-storage';
import seoReducer from '../reducers/seoSlice';
import consultationListReducer from '../reducers/consultation-list-slice';
import homeExploreExpertsReducer from '../reducers/homeExploreExpertsSlice';
import signalRReducer from '../reducers/signalr-slice';
import talentPoolRoleReducer from '../reducers/talent-pool/talent-pool-role-slice';
// import DarkModeSlice from '../reducers/theme';

/** Keep explore-experts tab selection in memory only — restore on SPA back, reset on hard refresh. */
const homeExploreExpertsTransform = createTransform(
  (inboundState: any) => {
    if (!inboundState) return inboundState;
    const { activeCategoryId: _omit, ...rest } = inboundState;
    return rest;
  },
  (outboundState: any) => {
    if (!outboundState) return outboundState;
    return { ...outboundState, activeCategoryId: null };
  },
  { whitelist: ['homeExploreExperts'] }
);

const persistConfig: any = {
  key: "root",
  version: 1,
  storage,
  whitelist: [
    'userDataSlice',
    'UserIdentity',
    'personalInfo',
    'education',
    'certificationInfo',
    'portfolio',
    'languageInfo',
    'experienceInfo',
    'websiteInfo',
    'basicDetails',
    'categorization',
    'packagesPricing',
    'firstStep',
    'secondStep',
    'stepper',
    'searchConsultation', // <-- persist search filters/search
    'applicationDataSlice', // <-- persist cached data (consultation details, location, skills, categories)
    'consultationList', // <-- persist consultation list data
    'homeExploreExperts', // <-- persist home explore experts categories + per-category cards (SWR)
    'signalR', // <-- persist SignalR state including unread count
  ],
  transforms: [homeExploreExpertsTransform],
  throttle: 1000,
  timeout: 2000,
};

const combinedReducer = combineReducers({
  UserIdentity: SwitchUserRole,
  HubConnectionObj: HubConnectionObj,
  UserAvatarImg: UserAvatarImg,
  userDataSlice: userDataSlice,
  notificationsSlice: notificationsSlice,
  chatSlice: chatSlice,
  applicationDataSlice: applicationDataSlice,
  personalInfo: personalInfoReducer,
  education: educationReducer,
  certificationInfo: certificationInfoReducer,
  portfolio: portfolioReducer,
  languageInfo: languageInfoReducer,
  experienceInfo: experienceInfoReducer,
  websiteInfo: websiteInfoReducer,
  basicDetails: basicDetailsReducer,
  categorization: categorizationReducer,
  packagesPricing: packagesPricingReducer,
  firstStep: firstStepReducer,
  secondStep: secondStepReducer,
  stepper: stepperReducer,
  searchConsultation: searchConsultationReducer,
  seo: seoReducer,
  consultationList: consultationListReducer,
  homeExploreExperts: homeExploreExpertsReducer,
  signalR: signalRReducer,
  talentPoolRole: talentPoolRoleReducer,
  // themeChange: DarkModeSlice
});

const persistedReducer = persistReducer(persistConfig, combinedReducer);

// redux-persist throttles writes, so a refresh right after a filter change reloaded null.
// sessionStorage is updated in the same turn as the action.
const rememberSearchFilters = (storeApi: any) => (next: any) => (action: any) => {
  const result = next(action);
  if (
    setSearchFilters.match(action)
    || clearSearchFilters.match(action)
    || setLastExploreTimestamp.match(action)
    || setLastDetailTimestamp.match(action)
    || action?.type === 'LOGOUT'
  ) {
    rememberSearchConsultation(storeApi.getState()?.searchConsultation);
  }
  return result;
};

export const Store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [
        FLUSH,
        REHYDRATE,
        PAUSE,
        PERSIST,
        PURGE,
        REGISTER,
        'persist/PERSIST', 
        'persist/REHYDRATE',
        'persist/PURGE',
        'LOGOUT', // <-- ignore logout action for serialization check
        'hubConn/StoreHubConnection',
        'hubConn/setConnectionStatus',
        'hubConn/setConnectionError',
        'hubConn/clearConnection',
        'signalr/setConnectionStatus',
        'signalr/setInitialized',
        'signalr/addMessage',
        'signalr/addNotification',
        'signalr/clearMessageQueue',
        'signalr/clearNotifications',
        'signalr/reset'
      ],
      ignoredActionPaths: ['result'],
      ignoredPaths: ['HubConnectionObj']
    }
  }).concat(rememberSearchFilters),
  devTools: process.env.NODE_ENV !== 'production',
});

// Create and export persistor for purging Redux Persist on logout
export const persistor = persistStore(Store);

// Define RootState and AppDispatch types
export type RootState = ReturnType<typeof combinedReducer>;
export type AppDispatch = typeof Store.dispatch;


